
// Form3.jsx (no auto-save)
import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./Form3.module.scss";
import saveIcon from "../../../assets/images/save-icon.svg";
import { Getcanleaddata, getKeySkills, PostRegister } from "../../../apiServices";

function MultiSelectAutocomplete({ options, placeholder, value, onChange, onBlur, isError, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const filteredOptions = options.filter(
    (opt) => opt.toLowerCase().includes(search.toLowerCase()) && !value.includes(opt)
  );

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
        if (onBlur) onBlur();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onBlur]);

  const handleSelect = (skill) => {
    if (!value.includes(skill)) onChange([...value, skill]);
    setSearch("");
    setOpen(false);
  };

  const handleRemove = (skillToRemove) => {
    onChange(value.filter((skill) => skill !== skillToRemove));
    if (onBlur) onBlur();
  };

  return (
    <div className={`${styles.multiSelectWrap}${isError ? " " + styles.err : ""}${disabled ? " " + styles.disabled : ""}`} ref={wrapRef}>
      <div className={styles.selectedSkills}>
        {value.map((skill, index) => (
          <span key={index} className={styles.skillTag}>
            {skill}
            <button type="button" className={styles.removeSkill} onClick={() => handleRemove(skill)} disabled={disabled}>×</button>
          </span>
        ))}
        <div className={styles.inputWrapper}>
          <input
            type="text"
            placeholder={value.length === 0 ? placeholder : ""}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            disabled={disabled}
            className={styles.skillInput}
          />
          <button type="button" className={styles.dropdownBtn} onClick={() => !disabled && setOpen(p => !p)} disabled={disabled}>
            {open ? "▴" : "▾"}
          </button>
        </div>
      </div>
      {open && (
        <div className={styles.dropdown}>
          <ul className={styles.optionList}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <li key={opt} className={styles.optionItem} onClick={() => handleSelect(opt)}>{opt}</li>
              ))
            ) : (
              <li className={styles.noOption}>{search ? "No matching skills found" : "Type to search skills"}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const parseSkills = (skillsString) => {
  if (!skillsString) return [];
  return skillsString.split(",").map(s => s.trim()).filter(Boolean);
};

export default function Form3({ onClose, id, recordType, form2Data, onSubmit, onBack, registerValidation, onValidationChange }) {
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [touched, setTouched] = useState(false);

  // --- FIX: stable refs ---
  const validateRef = useRef(null);
  const selectedSkillsRef = useRef(selectedSkills);
  selectedSkillsRef.current = selectedSkills;
  const touchedRef = useRef(touched);
  touchedRef.current = touched;

  const prevIsValidRef = useRef(true);

  useEffect(() => { fetchSkillsOptions(); }, []);
  useEffect(() => { if (id) fetchCandidateSkills(id); }, [id, recordType]);

  const fetchSkillsOptions = async () => {
    try {
      const response = await getKeySkills();
      if (response?.statuscode === 200 && response.results) {
        setSkillsOptions(response.results.map(item => item.skill));
      }
    } catch (error) { console.error("Error fetching skills options:", error); }
  };

  const fetchCandidateSkills = async (recordId) => {
    setLoading(true);
    try {
      const response = await Getcanleaddata(recordId, recordType);
      if (response?.code === 200) {
        const candidate = response.data;
        let skills = [];
        if (candidate.key_skill) skills = parseSkills(candidate.key_skill);
        else if (candidate.controller_skills) skills = parseSkills(candidate.controller_skills);
        else if (candidate.machine_skills) skills = parseSkills(candidate.machine_skills);
        else if (candidate.software_skills) skills = parseSkills(candidate.software_skills);
        setSelectedSkills(skills);
        if (skills.length > 0) setTouched(true);
      }
    } catch (error) { console.error("Error fetching candidate skills:", error); }
    finally { setLoading(false); }
  };

  // validate reads from ref - no deps
  // const validate = useCallback((silent = false) => {
  //   const skills = selectedSkillsRef.current;
  //   const isTouched = touchedRef.current;
  //   const errs = {};
  //   if (skills.length === 0 && isTouched) errs.skills = "At least one skill is required";
  //   if (!silent) setErrors(errs);
  //   return Object.keys(errs).length === 0;
  // }, []);

  // In Form3.js, update the validate function:

const validate = useCallback((silent = false) => {
  const skills = selectedSkillsRef.current;
  const isTouched = touchedRef.current;
  const errs = {};
  

  if (skills.length === 0) {
    
    if (!silent || !isTouched) {
      errs.skills = "At least one skill is required";
    }
  }
  
  if (!silent) setErrors(errs);
  return Object.keys(errs).length === 0;
}, []);

  validateRef.current = validate;

  // Register validation ONCE
  useEffect(() => {
    if (registerValidation) {
      registerValidation((silent = false) => validateRef.current(silent));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onValidationChange) {
      const isValid = selectedSkills.length > 0;
      if (prevIsValidRef.current !== isValid) {
        prevIsValidRef.current = isValid;
        onValidationChange();
      }
    }
  }, [selectedSkills, onValidationChange]);

  const handleSkillsChange = (skills) => {
    setSelectedSkills(skills);
    setTouched(true);
    if (skills.length > 0) setErrors({});
  };

  const handleBlur = () => {
    setTouched(true);
    validateRef.current(false);
  };

  const handleSave = useCallback(async () => {
    const skills = selectedSkillsRef.current;
    setTouched(true);
    const isValid = validateRef.current(false);

    if (isValid && skills.length > 0) {
      setLoading(true);
      try {
        const apiData = { key_skill: skills.join(',') };
        const response = await PostRegister(id, 3, apiData, recordType);
        if (response?.code === 200) {
          if (onSubmit) onSubmit(apiData, false);
          return true;
        } else {
          setErrors({ submit: response?.message || "Failed to save skills" });
          return false;
        }
      } catch (error) {
        console.error("Error saving form3:", error);
        setErrors({ submit: "Network error. Please try again." });
        return false;
      } finally { setLoading(false); }
    }
    return false;
  }, [id, recordType, onSubmit]);

  if (loading) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.formContent}><div className={styles.formTitle}>Loading Skills Data...</div></div>
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.formContent}>
        <div className={styles.formTitle}>Key Skills</div>
        {errors.submit && <div className={styles.errorSummary}>{errors.submit}</div>}

        <div className={styles.skillsSection}>
          <label className={styles.label}>Select Skills <span className={styles.required}>*</span></label>
          <MultiSelectAutocomplete
            options={skillsOptions}
            placeholder="Search and select skills..."
            value={selectedSkills}
            onChange={handleSkillsChange}
            onBlur={handleBlur}
            isError={!!errors.skills}
          />
          {errors.skills && <span className={styles.errorMsg}>{errors.skills}</span>}
        </div>

        {selectedSkills.length > 0 && (
          <div className={styles.skillsCount}>
            ✅ {selectedSkills.length} skill{selectedSkills.length !== 1 ? "s" : ""} selected
          </div>
        )}

        {selectedSkills.length === 0 && touched && (
          <div className={styles.infoMsg}>Please select at least one skill to continue</div>
        )}
      </div>

      <div className={styles.buttonWrapper}>
        <div className={styles.buttonGroup}>
          <button className={styles.btnCancel} onClick={onBack}>Back</button>
          <button className={styles.btnSave} onClick={handleSave}>
            <img src={saveIcon} alt="" width="16" height="16" />
            Save & Continue
          </button>
        </div>
      </div>
    </div>
  );
}